import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Clock } from 'lucide-react';
import { apiFetch } from '../api/client';
import { useShiftStatus } from '../hooks/useShiftStatus';

// ---------------------------------------------------------------------------
// ShiftClockWidget
// ---------------------------------------------------------------------------
// Real clock-in/clock-out (worker/lib/shifts.js, migrations/
// 0036_shift_sessions.sql). Mounted in TopNavBar's right-side slot
// alongside NotificationBell — same structural pattern (self-polling hook,
// click-to-open popover, click-outside/Escape to close) so it's reachable
// regardless of which MDI panel is focused, since TopNavBar renders once
// outside the panel system.
//
// Only rendered for SHIFT_TRACKING roles (frontdesk/waiter/chef/cashier —
// Dashboard.js decides whether to mount this at all; the component itself
// doesn't re-check role). Till-amount fields only show for selling roles
// (frontdesk/waiter/cashier) — chef's shift report has no till to
// reconcile, so asking a chef for an opening/closing till figure would be
// meaningless.
const SELLING_ROLES = new Set(['frontdesk', 'waiter', 'cashier']);

function money(cents) {
  return `KES ${((cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function elapsed(clockInAt) {
  if (!clockInAt) return '';
  const then = new Date(clockInAt.replace(' ', 'T') + 'Z').getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.max(0, Math.floor((Date.now() - then) / 60000));
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  return hrs > 0 ? `${hrs}h ${rem}m` : `${rem}m`;
}

function ShiftClockWidget({ user }) {
  const [open, setOpen] = useState(false);
  const [tillInput, setTillInput] = useState('');
  const [notesInput, setNotesInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const containerRef = useRef(null);

  const { shift, refresh } = useShiftStatus({ active: true });
  const isSelling = SELLING_ROLES.has(user?.role);

  useEffect(() => {
    if (!open) return undefined;
    function handlePointerDown(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    function handleKeyDown(event) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  const toggle = useCallback(() => {
    setError('');
    setTillInput('');
    setNotesInput('');
    setOpen((prev) => !prev);
  }, []);

  const clockIn = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      const body = {};
      if (isSelling && tillInput.trim()) {
        body.opening_till_cents = Math.round(parseFloat(tillInput) * 100);
      }
      const res = await apiFetch('/shifts/clock-in', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to clock in');
      await refresh();
      setOpen(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }, [isSelling, tillInput, refresh]);

  const clockOut = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      const body = { notes: notesInput || undefined };
      if (isSelling && tillInput.trim()) {
        body.closing_till_cents = Math.round(parseFloat(tillInput) * 100);
      }
      const res = await apiFetch('/shifts/clock-out', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to clock out');
      await refresh();
      setOpen(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }, [isSelling, tillInput, notesInput, refresh]);

  const isOpen = !!shift;

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={toggle}
        aria-haspopup="true"
        aria-expanded={open}
        className={`flex items-center gap-1.5 px-2.5 h-9 rounded text-xs font-medium ${
          isOpen ? 'text-shell-text bg-green-600/20 hover:bg-green-600/30' : 'text-shell-text hover:bg-shell-dark'
        }`}
      >
        <Clock className="w-4 h-4" aria-hidden="true" />
        {isOpen ? `On shift · ${elapsed(shift.clock_in_at)}` : 'Start Shift'}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-72 rounded-lg bg-white text-gray-800 shadow-xl ring-1 ring-black/5 z-50 p-4 space-y-3">
          {!isOpen ? (
            <>
              <div className="text-sm font-semibold">Start Shift</div>
              {isSelling && (
                <label className="block text-xs text-gray-600">
                  Opening till (KES)
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={tillInput}
                    onChange={(e) => setTillInput(e.target.value)}
                    placeholder="0.00"
                    className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                  />
                </label>
              )}
              {error && <div className="text-xs text-red-600">{error}</div>}
              <button
                type="button"
                disabled={busy}
                onClick={clockIn}
                className="w-full py-2 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 rounded disabled:opacity-50"
              >
                {busy ? 'Starting…' : 'Start Shift'}
              </button>
            </>
          ) : (
            <>
              <div className="text-sm font-semibold">End Shift</div>
              <div className="text-xs text-gray-700">
                Started {shift.clock_in_at} ({elapsed(shift.clock_in_at)} ago)
                {shift.opening_till_cents != null && <> · Opened with {money(shift.opening_till_cents)}</>}
              </div>
              {isSelling && (
                <label className="block text-xs text-gray-600">
                  Closing till (KES)
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={tillInput}
                    onChange={(e) => setTillInput(e.target.value)}
                    placeholder="0.00"
                    className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                  />
                </label>
              )}
              <label className="block text-xs text-gray-600">
                Notes (optional)
                <input
                  type="text"
                  value={notesInput}
                  onChange={(e) => setNotesInput(e.target.value)}
                  className="mt-1 w-full border border-gray-300 rounded px-2 py-1.5 text-sm"
                />
              </label>
              {error && <div className="text-xs text-red-600">{error}</div>}
              <button
                type="button"
                disabled={busy}
                onClick={clockOut}
                className="w-full py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded disabled:opacity-50"
              >
                {busy ? 'Ending…' : 'End Shift'}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default ShiftClockWidget;
